import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './PromotionCarousel.css';
import { ensureHttps } from '../utils/url';

// Cast icons to proper React element types to avoid TypeScript JSX component issues
const ChevronLeftIcon = FaChevronLeft as React.ElementType;
const ChevronRightIcon = FaChevronRight as React.ElementType;

interface Promotion {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  linkUrl?: string;
  type: 'promotion' | 'event' | 'advertisement' | 'announcement';
  startDate?: string;
  endDate?: string;
  clicks: number;
  views: number;
  isActive: boolean;
}

interface PromotionCarouselProps {
  promotions: Promotion[];
  onPromotionClick?: (promotionId: string) => void;
}

const PromotionCarousel: React.FC<PromotionCarouselProps> = ({ promotions, onPromotionClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Filter active promotions within date range
  const activePromotions = promotions.filter(promotion => {
    if (!promotion.isActive) return false;
    
    const now = new Date();
    const startDate = promotion.startDate ? new Date(promotion.startDate) : null;
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;
    
    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;
    
    return true;
  });

  useEffect(() => {
    if (!isAutoPlaying || activePromotions.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === activePromotions.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, activePromotions.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === activePromotions.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? activePromotions.length - 1 : prevIndex - 1
    );
  };

  const handlePromotionClick = (promotion: Promotion) => {
    if (onPromotionClick) {
      onPromotionClick(promotion.id);
    }
    if (promotion.linkUrl) {
      window.open(promotion.linkUrl, '_blank');
    }
  };

  const getTypeIcon = (type: string) => {
    // Use simple text icons to avoid TypeScript JSX component issues
    switch (type) {
      case 'promotion':
        return <span className="type-icon promotion">📢</span>;
      case 'event':
        return <span className="type-icon event">📅</span>;
      case 'advertisement':
        return <span className="type-icon advertisement">📢</span>;
      case 'announcement':
        return <span className="type-icon announcement">📢</span>;
      default:
        return <span className="type-icon">📢</span>;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'promotion':
        return '#ff6b6b';
      case 'event':
        return '#4ecdc4';
      case 'advertisement':
        return '#45b7d1';
      case 'announcement':
        return '#96ceb4';
      default:
        return '#ff6b6b';
    }
  };

  if (activePromotions.length === 0) {
    return null;
  }

  return (
    <div className="promotion-carousel">
      <div className="carousel-container">
        <button 
          className="carousel-button prev" 
          onClick={prevSlide}
          aria-label="Previous promotion"
        >
          <ChevronLeftIcon />
        </button>

        <div className="carousel-slide">
          <div 
            className="promotion-card"
            onClick={() => handlePromotionClick(activePromotions[currentIndex])}
            style={{ cursor: activePromotions[currentIndex].linkUrl ? 'pointer' : 'default' }}
          >
            <div className="promotion-header">
              <div className="promotion-type">
                {getTypeIcon(activePromotions[currentIndex].type)}
                <span 
                  className="type-label"
                  style={{ color: getTypeColor(activePromotions[currentIndex].type) }}
                >
                  {activePromotions[currentIndex].type.charAt(0).toUpperCase() + 
                   activePromotions[currentIndex].type.slice(1)}
                </span>
              </div>
            </div>

            <div className="promotion-content">
              {activePromotions[currentIndex].imageUrl && (
                <div className="promotion-image">
                  <img 
                    src={ensureHttps(activePromotions[currentIndex].imageUrl)} 
                    alt={activePromotions[currentIndex].title}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <div className="promotion-text">
                <h3 className="promotion-title">
                  {activePromotions[currentIndex].title}
                </h3>
                <p className="promotion-description">
                  {activePromotions[currentIndex].description}
                </p>
                
                {activePromotions[currentIndex].linkUrl && (
                  <button className="learn-more-btn">
                    Learn More
                  </button>
                )}
              </div>
            </div>

            <div className="promotion-stats">
              <span className="stat">
                <i className="fas fa-eye"></i> {activePromotions[currentIndex].views} views
              </span>
              <span className="stat">
                <i className="fas fa-mouse-pointer"></i> {activePromotions[currentIndex].clicks} clicks
              </span>
            </div>
          </div>
        </div>

        <button 
          className="carousel-button next" 
          onClick={nextSlide}
          aria-label="Next promotion"
        >
          <ChevronRightIcon />
        </button>
      </div>

      {activePromotions.length > 1 && (
        <div className="carousel-dots">
          {activePromotions.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to promotion ${index + 1}`}
            />
          ))}
        </div>
      )}

      <div className="carousel-controls">
        <button 
          className={`control-btn ${isAutoPlaying ? 'active' : ''}`}
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
        >
          {isAutoPlaying ? 'Pause' : 'Play'} Auto-rotate
        </button>
      </div>
    </div>
  );
};

export default PromotionCarousel; 